// packages/api/src/hooks/admin/pricing-admin/useDeductibles.ts
// Phase 02.3-pricing-admin W3.2 — Admin deductibles (franquias 3-nível).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Deductible, DeductibleUseType, PaginatedResponse } from '@alamo/types';

import { apiClient } from '../../../client';
import { pricingAdminKeys, pricingChangeSetKeys } from '../../../query-keys';

const BASE = '/admin/pricing/deductibles';

export interface ListDeductiblesFilters {
  page?: number;
  limit?: number;
  subgroupId?: string;
  /** Migration 0127 — filter por modelo FIPE específico. */
  vehicleModelId?: string;
  useType?: string;
  regionId?: string;
  status?: string;
  /**
   * Phase 08.4 Plan 04 — filtra franquias com item próprio em CS deste status.
   * Backend resolve via EXISTS subquery em `change_set_items` (deductibles.service:97).
   */
  csStatus?: 'draft' | 'approval_pending';
  /**
   * Filtro combinado dos chips "Em rascunho"/"Aguardando aprovação": casa a
   * franquia quando o próprio `status` é X (entry-direct) OU quando há CS item
   * nesse status (edição de franquia publicada). Cobre os mesmos buckets dos
   * banners da seção. Backend: deductibles.service `pendingState`.
   */
  pendingState?: 'draft' | 'approval_pending';
}

export function useDeductibles(filters?: ListDeductiblesFilters) {
  const page = filters?.page ?? 1;
  const limit = filters?.limit ?? 20;
  return useQuery<PaginatedResponse<Deductible>>({
    queryKey: pricingAdminKeys.deductibles({
      ...(filters as Record<string, unknown> | undefined),
      page,
      limit,
    }),
    queryFn: async () => {
      const res = await apiClient.get<{ data: { count: number; rows: Deductible[] } }>(BASE, {
        params: { ...(filters ?? {}), page, limit },
      });
      const { count, rows } = res.data.data;
      return {
        items: rows,
        total: count,
        page,
        limit,
        totalPages: Math.max(1, Math.ceil(count / limit)),
      };
    },
  });
}

/**
 * Phase 08.4 Plan 04 — count-only query (banner amber global agregando CS ativos).
 * Usa `limit=1` no backend (paginate paga 1 row) e devolve `count` total — equivalente
 * ao pattern Pilar 3 `useAdjustments({ pageSize: 1, csStatus })` (PricingPilar3Page:119).
 * Independente do filtro corrente do usuário; sempre conta sobre o universo total.
 */
export function useDeductiblesCount(filters?: ListDeductiblesFilters) {
  return useQuery({
    queryKey: [
      ...pricingAdminKeys.deductibles(filters as Record<string, unknown> | undefined),
      'count-only',
    ] as const,
    queryFn: async () => {
      const res = await apiClient.get<{ data: { count: number; rows: Deductible[] } }>(BASE, {
        params: { limit: 1, ...(filters ?? {}) },
      });
      return res.data.data.count;
    },
  });
}

export function useDeductible(id: string) {
  return useQuery({
    queryKey: pricingAdminKeys.deductible(id),
    queryFn: async () => {
      const res = await apiClient.get<{ data: Deductible }>(`${BASE}/${id}`);
      return res.data.data;
    },
    enabled: Boolean(id),
  });
}

export interface UpsertDeductibleInput {
  id?: string;
  subgroupId?: string | null;
  /**
   * Migration 0127 — override por modelo FIPE específico (opcional).
   * Quando setado, esta row vence sobre a row de subgrupo no lookup do engine.
   */
  vehicleModelId?: string | null;
  /** Mirrors backend `DEDUCTIBLE_USE_TYPES`. 'all' = catch-all (casa qualquer uso). */
  useType: DeductibleUseType;
  regionId?: string | null;
  /**
   * Condições especiais exigidas (matching). Opcionais, default false. `true` =
   * a regra só casa veículos com aquela condição. Múltiplas marcadas = AND.
   */
  requiresArmored?: boolean;
  requiresKitGas?: boolean;
  requiresRecuperado?: boolean;
  requiresElectric?: boolean;
  fipeMin: string;
  fipeMax: string;
  /**
   * Decisão Fernando 2026-05-21 — fusão `deductibles` × `deductible_tier_adjustments`.
   * Deltas inline por pacote. Agora NULLABLE + both-or-neither por par:
   *   `null`/omitido = tier NÃO oferecido (só Básica). Enviar só um delta de um
   *   par → 422 (`VALIDATION_FAILED` bothOrNeither).
   *
   * DECIMAL(5,4) em [-1, 1] como string. Ex.: "-0.1000" = -10%.
   */
  reduzidaFranchiseDeltaPct?: string | null;
  reduzidaPremiumDeltaPct?: string | null;
  ampliadaFranchiseDeltaPct?: string | null;
  ampliadaPremiumDeltaPct?: string | null;
  // ESCR-200.11 — MAX é o único modo aceito (drop legacy).
  // Backend rejeita payload com `valueFixed` via `whitelist: true` no
  // ValidationPipe. CHECK `ded_must_be_max` exige NOT NULL.
  /** Modo MAX — % sobre FIPE em (0..1]. OBRIGATÓRIO. */
  percentOverFipe: string;
  /** Modo MAX — piso em R$ (> 0). OBRIGATÓRIO. */
  minValueBrl: string;
  // Migration 0132 (2026-05-21) — `percentReduction` removido.
  doubledDays?: number;
}

export function useUpsertDeductible() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertDeductibleInput) => {
      if (input.id) {
        const { id, ...body } = input;
        const res = await apiClient.put<{ data: Deductible }>(`${BASE}/${id}`, body);
        return res.data.data;
      }
      const res = await apiClient.post<{ data: Deductible }>(BASE, input);
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingAdminKeys.all });
      // Phase 08.4 Plan 04 — refletir badges + indicadores antes→depois em
      // FranchiseSection sem refetch manual quando a mutação dispara
      // auto-attach a CS ativo no backend.
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

export function useDeleteDeductible() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`${BASE}/${id}`);
      return { id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingAdminKeys.all });
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}

/**
 * Submit do entry (draft|rejected → approval_pending). Aceita ID via mutate(),
 * usado pelo botão "Salvar e enviar para aprovação" do DeductibleEditModal em
 * mode='create' (workflow.submit do useApprovalWorkflow precisa do id no path,
 * que ainda não existe no momento da instância).
 */
export function useSubmitDeductibleEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.post(`${BASE}/${id}/submit`);
      return { id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pricingAdminKeys.all });
      qc.invalidateQueries({ queryKey: pricingChangeSetKeys.all });
    },
  });
}
